import { AdminPagination as ZhPagination } from "@/views/components/AdminPagination";
import { normalizeOperationColumns } from "@/views/components/ProTable";
import { Alert, Button, Card, Form, Input, Select, Tag, Typography } from "antd";
import type { Customer, CustomerVo, Page, Ship, ShipApply, ShipApplyVo, User } from "@zhihaoscm/entity";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router";
import { Table as ZhTable, type TableColumnProps } from "zhihao-ui-react";

import { shippingRequestHttp } from "@/api";
import { customerService } from "@/api/services/customerService";
import { DEFAULT_VALUE } from "@/config";
import { APPLY_STATE } from "@/meta/logistics/ship";
import { useUserStore } from "@/stores/react/authStore";

export interface ShipCertificationQuery {
    page: number;
    size: number;
    keyword?: string;
    customerId?: number;
    state?: number[];
    sortKey?: string;
    sortOrder?: string;
}

export interface ShipCertificationRow {
    shipApply: Partial<ShipApply>;
    shipVo?: { ship?: Partial<Ship> };
    customer?: Partial<Customer>;
    // 详情页沿用该复合行类型展示审核人，列表本身不渲染审核人列。
    user?: Partial<User>;
}

export interface ShipCertificationPageProps {
    loadApplications?: (query: ShipCertificationQuery) => Promise<Page<ShipCertificationRow>>;
    loadCustomers?: (keyword: string) => Promise<Array<{ label: string; value: number }>>;
}

/** 复用 Vue 船舶认证审核分页接口，保留历史查询参数命名。 */
export async function loadShipCertification(query: ShipCertificationQuery): Promise<Page<ShipCertificationRow>> {
    const response = await shippingRequestHttp.service.get<Page<ShipCertificationRow>>("/ship/apply/paging", {
        params: query
    });
    return response.data;
}

/** 复用旧 Vue 认证单位远程下拉，只查询承运方客户并保持机构/实名组合文案。 */
export async function loadShipCertificationCustomers(
    keyword: string
): Promise<Array<{ label: string; value: number }>> {
    if (!keyword.trim()) return [];
    const result = await customerService.paging({
        page: 1,
        size: 10,
        param: keyword.trim(),
        role: 4,
        sortOrder: 1,
        selectAll: false
    });
    return (result.list || []).map((item: CustomerVo) => ({
        value: Number(item.customer.id),
        label: item.enterpriseCertification?.name
            ? `${item.enterpriseCertification.name} | ${item.customer.realName || DEFAULT_VALUE}`
            : item.customer.realName || String(item.customer.id)
    }));
}

/** 按旧 Vue ProTable 约定构造筛选参数，多选状态保持 number[]。 */
export function buildShipCertificationQuery(
    keyword: string,
    customerId: number | undefined,
    state: number[]
): ShipCertificationQuery {
    const nextQuery: ShipCertificationQuery = { page: 1, size: 10 };
    if (keyword.trim()) nextQuery.keyword = keyword.trim();
    if (customerId !== undefined) nextQuery.customerId = customerId;
    if (state.length) nextQuery.state = state;
    return nextQuery;
}

function text(value: unknown): string {
    return value == null || value === "" ? DEFAULT_VALUE : String(value);
}

function dateText(value: unknown): string {
    return typeof value === "string" && value ? value.replace("T", " ").slice(0, 16) : DEFAULT_VALUE;
}

/** 物流管理-认证审核列表，完整复用旧 Vue 的筛选、列与审核入口。 */
export function ShipCertificationPage({
    loadApplications = loadShipCertification,
    loadCustomers = loadShipCertificationCustomers
}: ShipCertificationPageProps) {
    // 沿用 Vue userStore.banButton：保留审核入口，但在全局按钮禁用权限下禁止提交操作。
    const banButton = useUserStore(user => user.banButton);
    const [rows, setRows] = useState<ShipCertificationRow[]>([]);
    const [total, setTotal] = useState(0);
    const [query, setQuery] = useState<ShipCertificationQuery>({ page: 1, size: 10 });
    const [keyword, setKeyword] = useState("");
    const [customerId, setCustomerId] = useState<number>();
    const [customerOptions, setCustomerOptions] = useState<Array<{ label: string; value: number }>>([]);
    const [customerLoading, setCustomerLoading] = useState(false);
    const [state, setState] = useState<number[]>([]);
    const [errorMessage, setErrorMessage] = useState("");

    useEffect(() => {
        let disposed = false;
        void loadApplications(query)
            .then(result => {
                if (disposed) return;
                setRows(result.list || []);
                setTotal(result.total || 0);
                setErrorMessage("");
            })
            .catch(() => {
                if (disposed) return;
                setRows([]);
                setTotal(0);
                setErrorMessage("认证审核列表加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [loadApplications, query]);

    const submitFilters = () => {
        setQuery(buildShipCertificationQuery(keyword, customerId, state));
    };

    /** 清空 ProTable 筛选草稿并恢复旧 Vue 的第一页参数。 */
    const resetFilters = () => {
        setKeyword("");
        setCustomerId(undefined);
        setCustomerOptions([]);
        setState([]);
        setQuery({ page: 1, size: 10 });
    };

    /** 认证单位使用旧 Vue filterable remote 行为，空关键词不请求接口。 */
    const searchCustomers = async (value: string) => {
        if (!value.trim()) {
            setCustomerOptions([]);
            return;
        }
        setCustomerLoading(true);
        try {
            setCustomerOptions(await loadCustomers(value));
        } catch {
            setCustomerOptions([]);
            setErrorMessage("认证单位查询失败");
        } finally {
            setCustomerLoading(false);
        }
    };

    // 保留 Vue ProTable 的创建时间排序参数，并转换为旧接口字段。
    const handleSortChange = useCallback(
        ({ prop, order }: { prop?: string; order: "ascending" | "descending" | null }) => {
            if (prop !== "createdTime") return;
            setQuery(current => {
                const nextQuery = { ...current, page: 1 };
                if (!order) {
                    delete nextQuery.sortKey;
                    delete nextQuery.sortOrder;
                } else {
                    nextQuery.sortKey = "created_time";
                    nextQuery.sortOrder = order === "ascending" ? "asc" : "desc";
                }
                return nextQuery;
            });
        },
        []
    );

    // 用 zhihao-ui-react 的列契约承接旧 Vue 船舶认证 ProTable 字段和审核插槽。
    const columns = useMemo<TableColumnProps<ShipCertificationRow>[]>(
        () => [
            {
                key: "mmsi",
                label: "MMSI",
                width: 134,
                prop: "mmsi",
                render: ({ row }) => (
                    <Link to={`/logistics/shipManage/shipCertification/info/${row.shipApply.id}`}>
                        {text(row.shipApply.shipId)}
                    </Link>
                )
            },
            {
                key: "name",
                label: "AIS船名",
                minWidth: 176,
                prop: "name",
                render: ({ row }) => text(row.shipVo?.ship?.cnname || row.shipVo?.ship?.name)
            },
            {
                key: "customer",
                label: "认证单位",
                minWidth: 134,
                prop: "customerId",
                render: ({ row }) => {
                    const institution = row.customer?.institutionName;
                    const realName = row.customer?.realName;
                    return institution ? `${institution}-${text(realName)}` : text(realName);
                }
            },
            {
                key: "createdTime",
                label: "创建时间",
                width: 188,
                prop: "createdTime",
                sortable: true,
                render: ({ row }) => dateText(row.shipApply.createdTime)
            },
            {
                key: "state",
                label: "状态",
                width: 120,
                prop: "shipApply.state",
                render: ({ row }) => (
                    <Tag>{APPLY_STATE.getIntroByCode(Number(row.shipApply.state)) || DEFAULT_VALUE}</Tag>
                )
            },
            {
                key: "operation",
                label: "操作",
                width: 240,
                prop: "operation",
                fixed: "right",
                render: ({ row }) =>
                    Number(row.shipApply.state) === APPLY_STATE.PENDING_APPROVAL.code ? (
                        <Link to={`/logistics/shipManage/shipCertification/apply/${row.shipApply.id}`}>
                            <Button aria-label={`审核-${row.shipApply.id}`} disabled={banButton} type="link">
                                审核
                            </Button>
                        </Link>
                    ) : null
            }
        ],
        [banButton]
    );

    return (
        <section aria-label="ship-certification-page" className="flex min-h-full flex-col gap-4">
            <Card>
                <ZhTable<ShipCertificationRow>
                    rowKey={row => String(row.shipApply.id)}
                    data={rows}
                    columns={normalizeOperationColumns(columns)}
                    emptyText="暂无认证审核记录"
                    pagination={false}
                    onSortChange={handleSortChange}
                    isShowResetBtn={false}
                    isShowSearchBtn={false}
                    tableHeader={
                        <div className="admin-table-composed-header">
                            <Typography.Title className="mb-4 mt-0" level={4}>
                                认证审核列表
                            </Typography.Title>

                            {errorMessage ? <Alert message={errorMessage} role="alert" type="error" /> : null}
                            <Form className="admin-table-filter-form mb-4" layout="inline" onFinish={submitFilters}>
                                <Form.Item label="MMSI或船名">
                                    <Input
                                        aria-label="MMSI或船名"
                                        maxLength={32}
                                        placeholder="请输入船舶名关键词或MMSI搜索"
                                        value={keyword}
                                        onChange={event => setKeyword(event.target.value)}
                                    />
                                </Form.Item>
                                <Form.Item label="认证单位">
                                    <Select
                                        allowClear
                                        showSearch
                                        aria-label="认证单位"
                                        filterOption={false}
                                        loading={customerLoading}
                                        options={customerOptions}
                                        placeholder="请选择认证单位"
                                        value={customerId}
                                        onChange={setCustomerId}
                                        onSearch={value => void searchCustomers(value)}
                                    />
                                </Form.Item>
                                <Form.Item label="认证状态">
                                    <Select
                                        allowClear
                                        aria-label="认证状态"
                                        mode="multiple"
                                        options={APPLY_STATE.options.map(option => ({
                                            label: option.label,
                                            value: Number(option.value)
                                        }))}
                                        placeholder="请选择状态"
                                        value={state}
                                        onChange={setState}
                                    />
                                </Form.Item>
                                <Button aria-label="搜索认证" htmlType="submit" type="primary">
                                    搜索
                                </Button>
                                <Button onClick={resetFilters}>重置</Button>
                            </Form>
                        </div>
                    }
                    append={
                        <div className="admin-table-composed-pagination">
                            <ZhPagination
                                current={query.page}
                                pageSize={query.size}
                                total={total}
                                onChange={(page, pageSize) =>
                                    setQuery(current => ({
                                        ...current,
                                        page: pageSize !== current.size ? 1 : page,
                                        size: pageSize
                                    }))
                                }
                            />
                        </div>
                    }
                />
            </Card>
        </section>
    );
}

// 保留 ShipApplyVo 名称导入关系，便于后续详情任务复用旧 VO 映射时不改接口层。
export type LegacyShipApplyVo = ShipApplyVo;

export default ShipCertificationPage;
